import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, FileUp, Search, AlertTriangle, CalendarClock, Layers } from 'lucide-react';
import API, { apiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ErpHeading, ErpStatus, ErpPagination, useErpList } from '../../components/erp/ErpShared';
import { ASSET_GROUPS, STATUS_META, DOC_STATE } from '../../components/erp/assetConfig';
import AssetForm from '../../components/erp/AssetForm';
import AssetDetail from '../../components/erp/AssetDetail';
import ImportWizard from '../../components/erp/ImportWizard';
import '../../components/erp/assets.css';

export default function AssetsPage({ moduleKey }) {
  const cfg = ASSET_GROUPS[moduleKey];
  const { permissions } = useOutletContext();
  const canWrite = permissions.includes(cfg.writePerm);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [statsVersion, setStatsVersion] = useState(0);
  const [form, setForm] = useState(null);      // {record}|{}  for create/edit
  const [detailId, setDetailId] = useState('');
  const [importing, setImporting] = useState(false);

  const query = useMemo(() => `${q ? `&q=${encodeURIComponent(q)}` : ''}${category ? `&category=${encodeURIComponent(category)}` : ''}${status ? `&status=${status}` : ''}`, [q, category, status]);
  const list = useErpList(`/erp/fleet/${cfg.group}`, query);
  const loadStats = useCallback(() => API.get('/erp/fleet/stats').then(r => setStats(r.data[cfg.statsKey])).catch(() => {}), [cfg.statsKey]);
  useEffect(() => { loadStats(); }, [loadStats, statsVersion]);
  const refreshAll = () => { list.reload(); setStatsVersion(v => v + 1); };

  const kpis = stats ? [
    { k: 'total', v: stats.total, l: `${cfg.title} total`, c: '#3b82f6' },
    { k: 'available', v: stats.by_status?.disponibil || 0, l: 'Disponibile', c: '#10b981' },
    { k: 'expiring', v: stats.documents_expiring, l: 'Documente expiră', c: '#f59e0b', icon: CalendarClock },
    { k: 'expired', v: stats.documents_expired, l: 'Documente expirate', c: '#f43f5e', icon: AlertTriangle },
  ] : [];
  const categories = stats ? Object.entries(stats.by_category) : [];

  return <>
    <ErpHeading title={cfg.title} count={stats ? `${stats.total} înregistrări` : undefined}>
      {canWrite && <div className="flex gap-2"><Button variant="outline" onClick={() => setImporting(true)} data-testid="asset-import-btn"><FileUp className="w-4 h-4 mr-2" />Import PDF</Button>
        <Button onClick={() => setForm({})} data-testid="asset-add-btn"><Plus className="w-4 h-4 mr-2" />Adaugă</Button></div>}
    </ErpHeading>

    <div className="asset-kpis" data-testid="asset-kpis">{kpis.map(k => <div className="asset-kpi" key={k.k} style={{ '--kpi-accent': k.c }} data-testid={`kpi-${k.k}`}>
      <p className="kpi-val" style={{ color: k.c }}>{k.v}</p><p className="kpi-lbl">{k.icon ? <k.icon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> : null}{k.l}</p></div>)}</div>

    <div className="flex flex-wrap gap-3 items-center mb-5">
      <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Caută după denumire sau nr. înmatriculare…" className="pl-9" data-testid="asset-search" /></div>
      <select value={status} onChange={e => setStatus(e.target.value)} className="bg-[#18181b] border border-[#3f3f46] rounded-md px-3 h-10 text-sm" data-testid="asset-status-filter">
        <option value="">Toate stările</option>{Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}</select>
    </div>

    {categories.length > 0 && <div className="cat-chips" data-testid="asset-cat-chips">
      <button className={`cat-chip ${!category ? 'active' : ''}`} onClick={() => setCategory('')} data-testid="cat-all"><Layers className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Toate</button>
      {categories.map(([c, n]) => <button key={c} className={`cat-chip ${category === c ? 'active' : ''}`} onClick={() => setCategory(category === c ? '' : c)} data-testid={`cat-${c}`}>{c}<span className="chip-n">{n}</span></button>)}</div>}

    <ErpStatus list={list} />
    {!list.loading && !list.error && <div className="asset-grid" data-testid="asset-grid">{list.items.map(a => {
      const st = STATUS_META[a.status] || STATUS_META.disponibil; const ds = a.doc_status || { state: 'none' };
      return <button key={a.id} className="asset-card" onClick={() => setDetailId(a.id)} data-testid={`asset-card-${a.id}`}>
        <div className="flex items-center justify-between gap-2 mb-3"><span className={`plate ${a.plate ? '' : 'none'}`}>{a.plate || 'FĂRĂ NR.'}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span></div>
        <p className="font-medium leading-tight break-words">{a.make || a.model ? `${a.make} ${a.model}`.trim() : a.name}</p>
        <p className="text-xs text-muted-foreground mt-1">{a.category}</p>
        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground"><span className={`dot ${ds.state}`} title={DOC_STATE[ds.state]} />{ds.soonest ? `exp. ${new Date(ds.soonest).toLocaleDateString('ro-RO')}` : DOC_STATE[ds.state]}</div>
      </button>;
    })}</div>}
    {!list.loading && !list.error && list.items.length > 0 && <ErpPagination list={list} />}

    {form && <AssetForm cfg={cfg} record={form.id ? form : null} onClose={() => setForm(null)} onSaved={refreshAll} />}
    {detailId && <AssetDetail cfg={cfg} id={detailId} canWrite={canWrite} onClose={() => setDetailId('')} onEdit={r => { setDetailId(''); setForm(r); }} onChanged={refreshAll} />}
    {importing && <ImportWizard onClose={() => setImporting(false)} onCommitted={refreshAll} />}
  </>;
}
